import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AdvisorAnalysisRow } from '../../../services/api';
import { formatTimeUTC6, formatDateTimeUTC6, formatFullDateTimeUTC6 } from '../../../utils/formatters';

interface Props {
  analyses: AdvisorAnalysisRow[];
  loading: boolean;
  onRefresh: () => void;
  showDate?: boolean;
}

const statusClass = (status: string): string => {
  if (status === 'ok') return 'verdict-allow';
  if (status === 'timeout') return 'verdict-failclose';
  return 'verdict-block';
};

const AdvisorAnalyses: React.FC<Props> = ({ analyses, loading, onRefresh, showDate = false }) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const toggle = (id: number) => setExpandedId(prev => (prev === id ? null : id));

  if (loading && analyses.length === 0) {
    return (
      <div className="analyst-reviews-section">
        <p className="analyst-reviews-empty">Loading advisor analyses...</p>
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <div className="analyst-reviews-section">
        <div className="analyst-reviews-header">
          <button className="reviews-refresh-btn" onClick={onRefresh}>↻ Refresh</button>
        </div>
        <p className="analyst-reviews-empty">
          No advisor analyses in this window. Run one from the <strong>Advisor</strong> page.
        </p>
      </div>
    );
  }

  return (
    <div className="analyst-reviews-section">
      <div className="analyst-reviews-header">
        <span className="reviews-count">{analyses.length} analyses</span>
        <button className="reviews-refresh-btn" onClick={onRefresh}>↻ Refresh</button>
      </div>
      <div className="analyst-reviews-table-wrap">
        <table className="analyst-reviews-table">
          <thead>
            <tr>
              <th></th>
              <th>Time</th>
              <th>Instrument</th>
              <th>Dir</th>
              <th>Position</th>
              <th>Status</th>
              <th className="hide-mobile">PDF</th>
              <th className="hide-mobile">Tools</th>
              <th className="hide-mobile">Lat</th>
            </tr>
          </thead>
          <tbody>
            {analyses.map(a => {
              const expanded = expandedId === a.id;
              const time = showDate ? formatDateTimeUTC6(a.created_at) : formatTimeUTC6(a.created_at);
              return (
                <React.Fragment key={a.id}>
                  <tr
                    className={`review-row ${expanded ? 'review-row-expanded' : ''}`}
                    onClick={() => toggle(a.id)}
                  >
                    <td className="td-chevron">{expanded ? '▾' : '▸'}</td>
                    <td className="td-time">{time}</td>
                    <td className="td-mono">{a.instrument}</td>
                    <td>
                      <span className={`dir-tag ${a.direction === 'BUY' ? 'dir-buy' : 'dir-sell'}`}>
                        {a.direction}
                      </span>
                    </td>
                    <td className="td-mono">
                      {a.has_position
                        ? `${a.position_side} @ ${a.entry_price ?? '?'}`
                        : <span className="dim">—</span>}
                    </td>
                    <td>
                      <span className={`verdict-badge ${statusClass(a.status)}`}>
                        {a.status === 'ok' ? 'OK' : a.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="td-mono hide-mobile">
                      {a.used_uploaded_report
                        ? (a.pdf_report_date ?? 'yes')
                        : <span className="dim">—</span>}
                    </td>
                    <td className="td-mono hide-mobile">{a.tools_used ?? '—'}</td>
                    <td className="td-mono hide-mobile">
                      {a.latency_ms != null ? `${(a.latency_ms / 1000).toFixed(1)}s` : '—'}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="review-detail-row">
                      <td colSpan={9}>
                        <div className="review-detail">
                          {a.status === 'ok' && a.analysis_markdown ? (
                            <div className="advisor-history-markdown">
                              <ReactMarkdown>{a.analysis_markdown}</ReactMarkdown>
                            </div>
                          ) : (
                            <div className="review-section">
                              <div className="review-section-title review-section-red">
                                {a.status === 'timeout' ? 'Timeout' : 'Error'}
                              </div>
                              <div className="review-reasoning">
                                {a.error_detail || 'No details captured.'}
                              </div>
                            </div>
                          )}
                          <div className="review-meta">
                            <span><b>id:</b> {a.id}</span>
                            <span><b>created:</b> {formatFullDateTimeUTC6(a.created_at)}</span>
                            {a.opened_at && <span><b>opened:</b> {a.opened_at}</span>}
                            {a.model && <span><b>model:</b> {a.model}</span>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdvisorAnalyses;
